import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MaintenancePage from "@/pages/MaintenancePage";

/**
 * MaintenanceGuard
 *
 * Renders {children} normally UNLESS:
 *   - mod_settings.maintenance_mode === true, AND
 *   - the current visitor is NOT an authenticated admin, AND
 *   - the current route is a public route (i.e. not under /admin or /500).
 *
 * When those conditions hold, the whole app is replaced with <MaintenancePage>.
 *
 * The admin always sees the normal site so they can keep editing content while
 * public visitors see the maintenance splash.
 */
export default function MaintenanceGuard({ children }) {
    const location = useLocation();
    const { user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loaded, setLoaded] = useState(false);

    // Fetch mod-info once on mount + poll every 60s so the admin's changes
    // reach public visitors reasonably quickly without a full reload.
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data } = await api.get("/mod-info");
                if (mounted) setSettings(data);
            } catch {
                if (mounted) setSettings({});
            } finally {
                if (mounted) setLoaded(true);
            }
        };
        load();
        const t = setInterval(load, 60000);
        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, []);

    // Routes that should NEVER be replaced by the maintenance page.
    const path = location.pathname || "/";
    const isAdminRoute = path.startsWith("/admin");
    const isServerErrorPreview = path === "/500";
    const bypass = isAdminRoute || isServerErrorPreview;

    // Until mod-info is fetched we don't know whether maintenance is on.
    // Render children on admin routes immediately (fastest admin access),
    // but for public routes wait to avoid a flash of the real content.
    if (!loaded) {
        if (bypass) return children;
        return (
            <div
                data-testid="maintenance-loading"
                className="min-h-screen bg-[color:var(--bg-base)]"
            />
        );
    }

    const maintenanceOn = !!settings?.maintenance_mode;
    // user === null means "still checking auth"; false = anonymous; object = authed
    const isAdmin = !!(user && user.role === "admin");

    if (maintenanceOn && !isAdmin && !bypass) {
        return <MaintenancePage settings={settings} />;
    }

    return children;
}
